import { ChangeDetectionStrategy, Component, inject, OnInit, output, viewChild } from '@angular/core';
import { ProjectCompletionDetailsComponent } from '@app/cem/project/details/project-completion-details/project-completion-details.component';
import { ProjectDrillingDetailsComponent } from '@app/cem/project/details/project-drilling-details/project-drilling-details.component';
import { ProjectPondBuildComponent } from '@app/cem/project/details/project-pond-build/project-pond-build.component';
import { ProjectState } from '@app/cem/project/services/state/project-state';
import { IProjectTab } from '@app/shared/interfaces/iproject-tab';
import { ProjectBaseScenarioTabItemComponent } from 'app/cem/project/content/project-base-scenario-tabitem.component';
import { ProjectScreenBehaviorService } from 'app/cem/project/services/project-screenbehavior.service';
import { ProjectStatusId } from 'app/shared/data/project-enums';
import { GasLiftLineComponent } from '../../details/gas-lift-line/gas-lift-line.component';
import { PipelineComponent } from '../../details/pipeline/pipeline.component';
import { ProductionVolumesComponent } from '../../details/production-volumes/production-volumes.component';
import { ProjectPadConstructionDetailsComponent } from '../../details/project-pad-construction-details/project-pad-construction-details.component';
import { ProjectTankBatteryDetailsComponent } from '../../details/project-tank-battery-details/project-tank-battery-details.component';
import { ProjectWellPadDetailsComponent } from '../../details/project-well-pad-details/project-well-pad-details.component';
import { WellConnectSurfaceEquipmentComponent } from '../../details/well-connect-surface-equipment/well-connect-surface-equipment.component';
import { ProjectFlowlineDetailsComponent } from './../../details/project-flowline-details/project-flowline-details.component';
import { ProjectWellHookupDetailsComponent } from './../../details/project-well-hookup-details/project-well-hookup-details.component';

@Component({
  selector: 'app-project-details',
  templateUrl: './project-details.component.html',
  styleUrls: ['./project-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProductionVolumesComponent,
    PipelineComponent,
    GasLiftLineComponent,
    WellConnectSurfaceEquipmentComponent,
    ProjectDrillingDetailsComponent,
    ProjectTankBatteryDetailsComponent,
    ProjectCompletionDetailsComponent,
    ProjectPondBuildComponent,
    ProjectPadConstructionDetailsComponent,
    ProjectFlowlineDetailsComponent,
    ProjectWellHookupDetailsComponent,
    ProjectWellPadDetailsComponent,
  ],
})
export class ProjectDetailsComponent extends ProjectBaseScenarioTabItemComponent implements OnInit {
  public screenBehaviors = inject(ProjectScreenBehaviorService);
  private state = inject(ProjectState);

  readonly _projectDrillingDetailsComponent = viewChild(ProjectDrillingDetailsComponent);
  readonly _projectCompletionDetailsComponent = viewChild(ProjectCompletionDetailsComponent);
  readonly _projectPondBuildComponent = viewChild(ProjectPondBuildComponent);
  readonly _projectFlowlineDetailsComponent = viewChild(ProjectFlowlineDetailsComponent);
  readonly _projectWellHookupDetailsComponent = viewChild(ProjectWellHookupDetailsComponent);
  readonly _projectWellPadDetailsComponent = viewChild(ProjectWellPadDetailsComponent);
  readonly _projectPadConstructionDetailsComponent = viewChild(ProjectPadConstructionDetailsComponent);

  public thisProjectId: number;
  public thisProjectTypeId: number;
  public thisProjectTab: IProjectTab;

  public isFacilitiesProject: boolean;
  public isDrillingProject: boolean;
  public isTankBatteryProject: boolean;
  public isCompletionProject: boolean;
  public isFlowlineProject: boolean;
  public isWellHookupProject: boolean;
  public isWellPadProject: boolean;
  public isPadConstructionProject: boolean;
  public parametersDrillingTabTdv: boolean = false;
  public parametersCompletionTabTdv: boolean = false;
  public parametersPondTabTdv: boolean = false;
  public parametersFlowlineTabTdv: boolean = false;
  public parametersWellHookupTabTdv: boolean = false;
  public parametersWellPadTabTdv: boolean = false;
  public parametersPadConstructionTabTdv: boolean = false;
  public formState: boolean = false;
  public disableAllEdits: boolean;
  public projectStatusId: number;

  // emitter tabTdv provides touched-dirty-valid status of the form in this tab
  readonly tabTdv = output<boolean>();
  readonly saveComplete = output<boolean>();

  constructor() {
    super();
  }

  ngOnInit() {
    this.thisProjectId = this.projectTab().ProjectId;
    this.thisProjectTypeId = this.projectTab().ProjectTypeId;
    this.thisProjectTab = this.projectTab();
    this.isFacilitiesProject = false;
    this.isDrillingProject = false;
    this.isTankBatteryProject = false;
    this.isCompletionProject = false;
    this.isFlowlineProject = false;
    this.isWellHookupProject = false;
    this.isWellPadProject = false;
    this.isPadConstructionProject = false;
    let status = this.state.data.project.ProjectStatusId;
    this.projectStatusId = status;

    if (status === ProjectStatusId.Closed || status === ProjectStatusId.Cancelled) {
      this.disableAllEdits = true;
    } else {
      this.disableAllEdits = false;
    }
  }

  public chooseFacilities(): void {
    this.isFacilitiesProject = true;
    this.isDrillingProject = false;
    this.isTankBatteryProject = false;
    this.isCompletionProject = false;
    this.isFlowlineProject = false;
    this.isWellHookupProject = false;
    this.isWellPadProject = false;
    this.isPadConstructionProject = false;
  }

  public chooseDrilling(): void {
    this.isFacilitiesProject = false;
    this.isDrillingProject = true;
    this.isTankBatteryProject = false;
    this.isCompletionProject = false;
    this.isFlowlineProject = false;
    this.isWellHookupProject = false;
    this.isWellPadProject = false;
    this.isPadConstructionProject = false;
  }

  public chooseTankBattery(): void {
    this.isFacilitiesProject = false;
    this.isDrillingProject = false;
    this.isTankBatteryProject = true;
    this.isCompletionProject = false;
    this.isFlowlineProject = false;
    this.isWellHookupProject = false;
    this.isWellPadProject = false;
    this.isPadConstructionProject = false;
  }

  public chooseCompletion(): void {
    this.isFacilitiesProject = false;
    this.isDrillingProject = false;
    this.isTankBatteryProject = false;
    this.isCompletionProject = true;
    this.isFlowlineProject = false;
    this.isWellHookupProject = false;
    this.isWellPadProject = false;
    this.isPadConstructionProject = false;
  }

  public chooseFlowline(): void {
    this.isFacilitiesProject = false;
    this.isDrillingProject = false;
    this.isTankBatteryProject = false;
    this.isCompletionProject = false;
    this.isFlowlineProject = true;
    this.isWellHookupProject = false;
    this.isWellPadProject = false;
    this.isPadConstructionProject = false;
  }

  public chooseWellHookup(): void {
    this.isFacilitiesProject = false;
    this.isDrillingProject = false;
    this.isTankBatteryProject = false;
    this.isCompletionProject = false;
    this.isFlowlineProject = false;
    this.isWellHookupProject = true;
    this.isWellPadProject = false;
    this.isPadConstructionProject = false;
  }

  public chooseWellPad(): void {
    this.isFacilitiesProject = false;
    this.isDrillingProject = false;
    this.isTankBatteryProject = false;
    this.isCompletionProject = false;
    this.isFlowlineProject = false;
    this.isWellHookupProject = false;
    this.isWellPadProject = true;
    this.isPadConstructionProject = false;
  }

  public choosePadConstruction(): void {
    this.isFacilitiesProject = false;
    this.isDrillingProject = false;
    this.isTankBatteryProject = false;
    this.isCompletionProject = false;
    this.isFlowlineProject = false;
    this.isWellHookupProject = false;
    this.isWellPadProject = false;
    this.isPadConstructionProject = true;
  }

  public readDrillingParametersTabTdv(e: boolean): void {
    this.parametersDrillingTabTdv = e;
    this.emitFormTDV(e);
  }

  public readCompletionParametersTabTdv(e: boolean): void {
    this.parametersCompletionTabTdv = e;
    this.emitFormTDV(e);
  }

  public readPondBuildTabTdv(e: boolean): void {
    this.parametersPondTabTdv = e;
    this.emitFormTDV(e);
  }

  public readFlowlineParametersTabTdv(e: boolean): void {
    this.parametersFlowlineTabTdv = e;
    this.emitFormTDV(e);
  }

  public readWellHookupParametersTabTdv(e: boolean): void {
    this.parametersWellHookupTabTdv = e;
    this.emitFormTDV(e);
  }

  public readWellPadParametersTabTdv(e: boolean): void {
    this.parametersWellPadTabTdv = e;
    this.emitFormTDV(e);
  }

  public readPadConstructionParametersTabTdv(e: boolean): void {
    this.parametersPadConstructionTabTdv = e;
    this.emitFormTDV(e);
  }

  public emitFormTDV(e: boolean): void {
    if (e) {
      this.setTDV();
    } else {
      this.clearTDV();
    }
  }

  public saveParameters(e: any): void {
    if (this.parametersDrillingTabTdv) {
      this._projectDrillingDetailsComponent().saveParameters(e);
    }
    if (this.parametersCompletionTabTdv) {
      this._projectCompletionDetailsComponent().updateCompletionParameters(e);
    }
    if (this.parametersPondTabTdv) {
      this._projectPondBuildComponent().updateProjectPondParameters(e);
      this.saveComplete.emit(true);
    }
    if (this.parametersFlowlineTabTdv) {
      this._projectFlowlineDetailsComponent().updateFlowlineParameters(e);
      this.saveComplete.emit(true);
    }
    if (this.parametersWellHookupTabTdv) {
      this._projectWellHookupDetailsComponent().updateWellHookupParameters(e);
      this.saveComplete.emit(true);
    }
    if (this.parametersWellPadTabTdv) {
      this._projectWellPadDetailsComponent().updateWellPadParameters(e);
      this.saveComplete.emit(true);
    }
    if (this.parametersPadConstructionTabTdv) {
      this._projectPadConstructionDetailsComponent().updatePadConstructionParameters(e);
      this.saveComplete.emit(true);
    }
  }

  public completionsSaveComplete(e: boolean) {
    this.saveComplete.emit(e);
  }

  public drillingSaveComplete(e: boolean) {
    this.saveComplete.emit(e);
  }

  public cancelChanges(e: any): void {
    if (this.parametersDrillingTabTdv) {
      this._projectDrillingDetailsComponent().cancelChanges(e);
    }
    if (this.parametersCompletionTabTdv) {
      this._projectCompletionDetailsComponent().cancelChanges(e);
    }
    if (this.parametersPondTabTdv) {
      this._projectPondBuildComponent().cancelChanges(e);
    }
    if (this.parametersFlowlineTabTdv) {
      this._projectFlowlineDetailsComponent().cancelChanges(e);
    }
    if (this.parametersWellHookupTabTdv) {
      this._projectWellHookupDetailsComponent().cancelChanges(e);
    }
    if (this.parametersWellPadTabTdv) {
      this._projectWellPadDetailsComponent().cancelChanges(e);
    }
    if (this.parametersPadConstructionTabTdv) {
      this._projectPadConstructionDetailsComponent().cancelChanges(e);
    }
  }

  public runTabActivity(): void {
    //TODO - create enum for all constants referenced like this throughout the app
    if (this.thisProjectTypeId === 62) {
      this._projectWellHookupDetailsComponent().getWellHookupParameters(false);
    }

    if (this.thisProjectTypeId === 61) {
      this._projectWellPadDetailsComponent().getWellPadParameters(false);
    }
  }

  public setTDV(): void {
    let tdvState: boolean = true;
    this.tabTdv.emit(tdvState);
  }

  public clearTDV(): void {
    let tdvState: boolean = false;
    this.tabTdv.emit(tdvState);
  }
}